export const value = (row, key) => String(row?.data?.[key] ?? "").trim();
const norm = (v) => String(v || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().replace(/[_\s]+/g, " ").trim();

export function parseMoney(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : NaN;
  let s = String(v ?? "").replace(/R\$|BRL|USD|EUR|US\$|[$€\s]/gi, "");
  if (!s || !/^-?[\d.,]+$/.test(s)) return NaN;
  if (s.includes(",")) s = s.replace(/\./g, "").replace(",", ".");
  else if (/^-?\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, "");
  return Number(s);
}

export function parseDateForSort(v) {
  const s = String(v || "").trim();
  const br = s.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:[, ]+([0-2]\d):([0-5]\d)(?::([0-5]\d))?)?$/);
  if (br) {
    const [, d, m, y, hh = "00", mm = "00", ss = "00"] = br;
    const dt = new Date(+y, +m - 1, +d, +hh, +mm, +ss);
    return dt.getDate() === +d && dt.getMonth() === +m - 1 ? dt.getTime() : 0;
  }
  return /^\d{4}-\d{2}-\d{2}(?:T.*)?$/.test(s) ? Date.parse(s) || 0 : 0;
}

export function auditEvents(row) {
  const events = [];
  // Legacy positional writes can have put signatures in any auxiliary column.
  Object.values(row?.data || {}).forEach((raw) => {
    if (typeof raw !== "string" || !raw.trim().startsWith("[")) return;
    try {
      const list = JSON.parse(raw);
      if (Array.isArray(list)) events.push(...list.filter((e) => e && e.etapa && e.decisao && e.dataHora));
    } catch { /* Preserve malformed legacy data; never render it as a date. */ }
  });
  return events.sort((a, b) => parseDateForSort(a.dataHora) - parseDateForSort(b.dataHora));
}

export function getModifiedDate(row) {
  const candidates = [value(row, "Gestao Data Modificacao"), ...auditEvents(row).map((e) => e.dataHora)].filter((s) => parseDateForSort(s));
  if (candidates.length) return candidates.sort((a, b) => parseDateForSort(b) - parseDateForSort(a))[0];
  return ["Data Solicitação", "Timestamp"].map((k) => value(row, k)).find((s) => parseDateForSort(s)) || "";
}

export function detectStatus(row) {
  const raw = norm(value(row, "Status") || value(row, "AW Status"));
  const management = norm(value(row, "Gestao Status Financeiro"));
  const latest = {};
  auditEvents(row).forEach((e) => { latest[norm(e.etapa)] = norm(e.decisao); });
  if (/CANCEL/.test(raw + management)) return "Cancelada";
  if (/REJEIT|REPROV|REJECT/.test(raw + management) || Object.values(latest).some((s) => /REJEIT|REPROV|REJECT/.test(s))) return "Rejeitada";
  if (/PENDENTE|DEVOLVID|AJUSTE/.test(raw)) return "Pendente";
  if (/^APROVAD[OA] (?:PEL[OA] )?SUPERINTEND/.test(raw) || /^(APROVADO|APPROVED)$/.test(latest.SUPERINTENDENCIA || latest.SUPERINTENDENTE || "")) return "APROVADO SUPERINTENDENTE";
  if (/APROVAD.*GERENTE/.test(raw) || /^(APROVADO|APPROVED)$/.test(latest["GERENTE AREA"] || latest.GERENTE || "")) return "APROVADO GERENTE";
  if (/AGUARDANDO|ENVIADO/.test(raw) || latest.SOLICITANTE === "ENVIADO") return "ENVIADO";
  return raw || "Aguardando assinaturas";
}

export function finalPdfLink(row) {
  if (detectStatus(row) !== "APROVADO SUPERINTENDENTE") return "";
  const signatures = {};
  auditEvents(row).forEach((e) => { signatures[norm(e.etapa)] = e; });
  if (!["SOLICITANTE", "GERENTE AREA", "SUPERINTENDENCIA"].every((stage) => {
    const e = signatures[stage];
    return e?.hash && parseDateForSort(e.dataHora) && norm(e.decisao) === (stage === "SOLICITANTE" ? "ENVIADO" : "APROVADO");
  })) return "";
  // Only the canonical final-file field is authoritative. Generic PDF fields may be drafts.
  const link = value(row, "Gestao Link PDF Final");
  try {
    const url = new URL(link);
    return url.protocol === "https:" && url.hostname === "drive.google.com" && /^\/file\/d\/[^/]+/.test(url.pathname) ? url.href : "";
  } catch { return ""; }
}

export function financeiroDisplay(row) {
  // This marker is written only AFTER MailApp confirms sendEmail without throwing.
  const marker = value(row, "Gestao Status Financeiro");
  const sent = auditEvents(row).filter((e) => e.etapa === "FINANCEIRO" && e.decisao === "ENCAMINHADO" && e.pdfLink && parseDateForSort(e.dataHora)).pop();
  return sent ? `Encaminhado ao Financeiro em ${sent.dataHora}` : /^ENCAMINHADO AO FINANCEIRO EM \d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}$/.test(marker)
    ? marker : "Encaminhamento não comprovado";
}

export function duplicateRows(rows) {
  const groups = new Map();
  rows.forEach((row) => {
    const cpf = value(row, "CPF").replace(/\D/g, "");
    const destination = norm(value(row, "Destino"));
    const amount = parseMoney(value(row, "Total Diárias"));
    if (cpf.length !== 11 || !destination || !Number.isFinite(amount) || amount < 0) return;
    const key = JSON.stringify([cpf, destination, Math.round(amount * 100), norm(value(row, "Moeda") || "R$")]);
    if (!groups.has(key)) groups.set(key, new Set());
    groups.get(key).add(value(row, "Protocolo") || row.rowNumber);
  });
  return new Set(rows.filter((row) => {
    const key = JSON.stringify([value(row, "CPF").replace(/\D/g, ""), norm(value(row, "Destino")), Math.round(parseMoney(value(row, "Total Diárias")) * 100), norm(value(row, "Moeda") || "R$")]);
    return groups.get(key)?.size > 1;
  }).map((r) => r.rowNumber));
}
